import React from 'react';
import ReactApexChart from 'react-apexcharts';

class Energy extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      series: [0.00],
      lastUpdate: null,
      isConnected: false,
      options: {
        chart: {
          height: 350,
          type: 'radialBar',
          toolbar: {
            show: true,
          },
          animations: {
            enabled: true,
            speed: 500,
          }
        },
        plotOptions: {
          radialBar: {
            startAngle: 0,
            endAngle: 360,
            hollow: {
              margin: 4,
              size: '70%',
              background: 'transparent',
              dropShadow: {
                enabled: true,
                top: 3,
                left: 0,
                blur: 4,
                opacity: 0.24,
              },
            },
            track: {
              background: '#fff',
              strokeWidth: '97%',
              dropShadow: {
                enabled: true,
                top: -3,
                left: 0,
                blur: 4,
                opacity: 0.35,
              },
            },
            dataLabels: {
              show: true,
              name: {
                offsetY: -10,
                color: '#888',
                fontSize: '17px',
              },
              value: {
                formatter: (val) => val.toFixed(2) + " kWh",
                color: '#888',
                fontSize: '24px',
              },
            },
          },
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: ['#ABE5A1'],
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100],
          },
        },
        stroke: {
          lineCap: 'round',
        },
        labels: ['Energy (kWh)'],
        minValue: 0.01,
        maxValue: 10,
      },
    };

    this.socket = null;
    this.reconnectInterval = null;
    this.reconnectAttempts = 0;
  }

  componentDidMount() {
    this.connectWebSocket();
  }

  componentWillUnmount() {
    this.cleanupWebSocket();
  }

  connectWebSocket = () => {
    try {
      const wsUrl = process.env.REACT_APP_ENDPOINT_WS || 'ws://localhost:5000';
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('Energy WebSocket connected');
        this.setState({ isConnected: true });
        this.reconnectAttempts = 0;
        
        if (this.reconnectInterval) {
          clearInterval(this.reconnectInterval);
          this.reconnectInterval = null;
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const now = new Date();
          
          // Handle connection message (optional)
          if (message.type === 'connection') {
            console.log('Energy: WebSocket connection established');
            return;
          }
          
          // Handle real-time data
          let energyValue = 0;
          
          if (message.type === 'realtime' && message.energy !== undefined) {
            energyValue = parseFloat(message.energy.toFixed(2));
          } 
          // Handle legacy format
          else if (!message.type && message.energy !== undefined) {
            energyValue = parseFloat(message.energy.toFixed(2));
          }
          
          if (energyValue > 0) {
            this.setState({
              series: [energyValue],
              lastUpdate: now
            });
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      this.socket.onclose = (event) => {
        console.log(`Energy WebSocket disconnected: ${event.code}`);
        this.setState({ isConnected: false });
        
        // Attempt to reconnect with exponential backoff
        this.reconnectAttempts++;
        const delay = Math.min(30000, 1000 * Math.pow(2, this.reconnectAttempts));
        
        console.log(`Reconnecting in ${delay/1000} seconds... (Attempt ${this.reconnectAttempts})`);
        
        if (this.reconnectInterval) {
          clearTimeout(this.reconnectInterval);
        }
        
        this.reconnectInterval = setTimeout(() => {
          if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
            this.connectWebSocket();
          }
        }, delay);
      };

      this.socket.onerror = (error) => {
        console.error('Energy WebSocket error:', error);
        this.setState({ isConnected: false });
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      this.setState({ isConnected: false });
    }
  };

  cleanupWebSocket = () => {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  };

  render() {
    const { series, options, isConnected, lastUpdate } = this.state;
    const currentEnergy = series[0] || 0;

    // Calculate percentage for color coding
    const percentage = Math.min(100, (currentEnergy / 10) * 100);
    
    // Update chart colors based on energy level
    const updatedOptions = {
      ...options,
      fill: {
        ...options.fill,
        gradient: {
          ...options.fill.gradient,
          gradientToColors: [
            percentage > 75 ? '#EF4444' :  // Red for high
            percentage > 50 ? '#F59E0B' :  // Yellow for medium
            '#10B981'                      // Green for low
          ]
        }
      }
    };

    return (
      <div style={{ position: 'relative' }}>
               
        {/* Last Update Time */}
        {lastUpdate && (
          <div style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            fontSize: '12px',
            color: '#666',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '4px 8px',
            borderRadius: '4px',
            zIndex: 10
          }}>
            Updated: {lastUpdate.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        )}

        
        <div id="chart">
          <ReactApexChart
            options={updatedOptions}
            series={series}
            type="radialBar"
            height={350}
          />
        </div>

        {/* CSS Animation */}
        <style>{`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}</style>
      </div>
    );
  }
}

export default Energy;